import React, { useState, useEffect } from 'react';
import { requestWithdrawal, getWithdrawals, getBalance } from '../services/api';
import toast from 'react-hot-toast';

const Withdraw = () => {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [balance, setBalance] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, withdrawalsRes] = await Promise.all([
        getBalance(),
        getWithdrawals(),
      ]);
      if (balanceRes.data.success) setBalance(balanceRes.data.balance);
      if (withdrawalsRes.data.success) setWithdrawals(withdrawalsRes.data.withdrawals);
    } catch (error) {
      console.error('Failed to load withdrawal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) {
      toast.error('Please enter a valid BSC wallet address');
      return;
    }

    if (parseFloat(amount) > balance) {
      toast.error('Insufficient balance');
      return;
    }

    if (parseFloat(amount) < 10) {
      toast.error('Minimum withdrawal is 10 USDT');
      return;
    }

    setSubmitting(true);
    try {
      const response = await requestWithdrawal({
        amount: parseFloat(amount),
        destination_address: address,
      });

      if (response.data.success) {
        toast.success('Withdrawal request submitted! Awaiting admin approval.');
        setAmount('');
        setAddress('');
        fetchData();
      } else {
        toast.error(response.data.message || 'Withdrawal request failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error(error.response?.data?.message || 'Withdrawal request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge badge-warning',
      approved: 'badge badge-info',
      sent: 'badge badge-success',
      rejected: 'badge badge-danger',
      processing: 'badge badge-warning',
    };
    return badges[status] || 'badge badge-info';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading withdrawal information...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">Withdraw Funds</h1>
        <p className="dashboard-section-subtitle">Request payouts to your BSC wallet securely</p>
      </div>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Request Withdrawal</h2>
          <p className="dashboard-section-subtitle">
            Available Balance: <span className="withdraw-balance-highlight">{balance.toFixed(2)} USDT</span>
          </p>
        </div>

        <div className="dashboard-section-body">
          <form onSubmit={handleSubmit} className="withdraw-form">
            <div className="withdraw-form-grid">
              <div className="auth-field">
                <label className="auth-label">Amount (USDT)</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="auth-input"
                  placeholder="Min: 10 USDT"
                  step="0.01"
                  min="10"
                  disabled={submitting}
                />
              </div>

              <div className="auth-field withdraw-address-field">
                <label className="auth-label">Destination Wallet Address (BSC)</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="auth-input withdraw-address-input"
                  placeholder="0x..."
                  disabled={submitting}
                />
                <p className="auth-helper-text">
                  Send only to BSC (BEP-20) compatible wallet addresses
                </p>
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit-btn withdraw-submit-btn"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Request Withdrawal'}
            </button>
          </form>
        </div>
      </section>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Withdrawal History</h2>
          <p className="dashboard-section-subtitle">Your latest withdrawal requests</p>
        </div>

        <div className="dashboard-section-body">
          {withdrawals.length === 0 ? (
            <div className="styled-empty-state">
              <div className="empty-state-icon">
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="styled-empty-title">No withdrawal requests yet</p>
              <p className="styled-empty-subtitle">Submit your first withdrawal above</p>
            </div>
          ) : (
            <div className="market-table-wrap">
              <table className="table-dark w-full">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>TX Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((wd) => (
                    <tr key={wd.id}>
                      <td className="font-medium">{parseFloat(wd.amount).toFixed(2)} USDT</td>
                      <td className="font-mono text-xs">
                        {wd.destination_address.slice(0, 8)}...{wd.destination_address.slice(-6)}
                      </td>
                      <td>
                        <span className={getStatusBadge(wd.status)}>
                          {wd.status}
                        </span>
                      </td>
                      <td className="text-gray-400 text-sm">
                        {new Date(wd.requested_at).toLocaleDateString()}
                      </td>
                      <td className="font-mono text-xs text-gray-500">
                        {wd.tx_hash ? `${wd.tx_hash.slice(0, 8)}...` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Withdraw;